import React, { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  FormControlLabel,
  Radio,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  FormHelperText,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material"; // Add TextField to the import statement
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { styled } from "@mui/system";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom"; // Import useNavigate from react-router-dom
import {
  editUserExperience,
  getMasterData,
  getUserDetails,
  logoutClear,
  updateUserExperience,
} from "../../redux/actions/appActions";
import { useDispatch, useSelector } from "react-redux";
import MultiSelect from "../common/MultiSelect";
import { getKeyValuePairs } from "../../redux/services/utils";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import moment from "moment";
import { ROUTE_COMPLETE_PROFILE, ROUTE_SKILLSET_NEW, ROUTE_MAIN } from "../../constants/routes";
import { SET_FUNCTIONS, SET_USER_EXPERIENCE } from "../../redux/actions/actionTypes";
import { RootState } from "../../redux/types/reduxState";
import { CssSelectTextField } from "../common/CssTextField";
import { JOB_TYPES, WORK_MODES } from "../../constants/AppVarConstants";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  backgroundColor: "rgb(255, 137, 96)",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Avatar1 = styled(Avatar)({
  width: "51px",
  height: "51px",
});

const InputField = ({ control, name }: any) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          error={!!error}
          helperText={error?.message || " "}
          placeholder="Write here"
          variant="outlined"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "95px",
              boxShadow: "inset 0 0 11px 2px #dfd9d9",
              "& fieldset": {
                border: "none",
              },
              "& input": {
                padding: "12px 24px",
              },
            },
            marginTop: "10px",
          }}
        />
      )}
    />
  );
};

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

const StyledTextBox1 = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

const JobType = styled("div")(() => ({
  display: "flex",
  justifyContent: "flex-start",
  gap: "8px",
  fontSize: "16px",
}));

type FormValues = Yup.InferType<any>;

const experienceSchema = Yup.object().shape({
  companyName: Yup.string().required("Please enter company name"),
  designation: Yup.string().required("Please enter designation"),
  function: Yup.mixed().required("Please enter function"),
  jobType: Yup.array().min(1, "Please select job type"),
  workMode: Yup.array().min(1, "Please select work mode"),
  working: Yup.boolean().notRequired(),
  startDate: Yup.mixed().required("Joining date is required"),
  endDate: Yup.date()
    .when("working", {
      is: false,
      then: (schema: any) => schema.required("End date is required").typeError("Invalid date format"),
      otherwise: (schema: any) => schema.nullable(),
    })
    .test("is-greater", "End date must be greater than start date", function (value) {
      const { startDate, working } = this.parent;
      if (working) {
        return true;
      }
      return !value || !startDate || new Date(value) > new Date(startDate);
    }),
  summary: Yup.string().notRequired(),
  link: Yup.string().notRequired(),
});

const schema = Yup.object().shape({
  experiences: Yup.array().of(experienceSchema),
});

const experience = {
  companyName: "",
  designation: "",
  function: null,
  jobType: [],
  workMode: [],
  working: false,
  startDate: null,
  endDate: null,
  summary: "",
  link: "",
  experienceId: "",
};

const defaultValues = {
  experiences: [experience],
};

const ExperienceNew = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { functions } = useSelector((state: any) => state.app);
  const [SelectedJob, setSelectedJob] = useState("Yes"); // Set default selection
  const [selectedTab, setSelectedTab] = useState(0); // Define selectedTab state variable
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const userExeperience = useSelector((state: RootState) => state.app.userExeperience);

  const Logout = () => {
    dispatch(logoutClear(()=>{
        navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const { handleSubmit, control, watch, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  const {
    fields: experienceFields,
    append: appendExperience,
    remove: removeExperience,
  } = useFieldArray({
    control,
    name: "experiences",
  });

  const handleJobChange = (event: any) => {
    setSelectedJob(event.target.value);
  };

  const handleTabChange = (event: any, newValue: any) => {
    // Define handleTabChange function
    setSelectedTab(newValue);
  };

  useEffect(() => {
    dispatch(getMasterData("FUNCTIONS", SET_FUNCTIONS));
    dispatch(getUserDetails(SET_USER_EXPERIENCE, "experience"));
  }, []);

  useEffect(() => {
    if (userExeperience.length) {
      if (userExeperience?.length) {
        setSelectedJob("Yes");
      }
      const data = userExeperience.map((el: any) => {
        return {
          experienceId: el?.experienceId,
          companyName: el?.companyName,
          designation: el?.designationName,
          function: el?.departmentName ? { label: el?.departmentName, value: el?.departmentName } : null,
          jobType: getKeyValuePairs(el?.jobType),
          workMode: getKeyValuePairs(el?.workMode),
          working: el?.isCurrentWorkingHere,
          startDate: el?.startDate,
          endDate: el?.endDate,
          summary: el?.description,
          link: el?.workLink,
        };
      });
      setValue("experiences", data);
    }
  }, [userExeperience]);

  const jobTypes = ["Yes", "No"];
  const degrees = ["EDUCATION & WORK EX.", "SPECIALIZATIONS", "VIDEO", "PERSONALITY"];

  const styles = {
    button: {
      padding: "15px 50px",
      color: "#777777",
      backgroundColor: "#fef1ed",
      borderRadius: "99px",
      fontSize: "14px",
      fontWeight: "600",
      textTransform: "capitalize",
      width: "100%",
      marginTop: "20px",
      "&:hover": {
        backgroundColor: "#ffffff",
      },
    },
  };

  const onSubmit = (data: FormValues) => {
    const experience = data?.experiences?.map((el: any) => {
      return {
        experienceId: el?.experienceId,
        companyName: el?.companyName,
        // location: "City, Country",
        designationName: el?.designation,
        // department: el?.department?.value,
        // duration: el?.duration,
        // year: el?.year,
        startDate: moment(el?.startDate).format("YYYY-MM-DD"),
        endDate: moment(el?.endDate).format("YYYY-MM-DD"),
        isCurrentWorkingHere: el?.working,
        description: el?.summary,
        departmentName: el?.function?.value || el?.function?.label || el?.function,
        jobType: el?.jobType?.map((el: { value: string }) => el?.value),
        workMode: el?.workMode?.map((el: { value: string }) => el?.value),
        workLink: el?.link,
        // industryName: el?.healthcare,
      };
    });

    const isEqual = (edu1: any, edu2: any) => {
      return edu1.experienceId === edu2.experienceId;
    };

    const missingExperience = userExeperience
      ?.filter((userEdu: any) => {
        return !experience.some((edu: any) => isEqual(edu, userEdu));
      })
      .map((el: any) => ({
        ...el,
        deleted: true,
      }));

    const updatedExperience = [...experience, ...missingExperience];
    const isEdit = location?.search?.includes("edit") || localStorage?.getItem("EDIT");
    if (userExeperience?.length) {
      dispatch(
        editUserExperience(updatedExperience, () => navigate(isEdit ? `${ROUTE_COMPLETE_PROFILE}` : ROUTE_SKILLSET_NEW))
      );
    } else {
      dispatch(
        updateUserExperience(experience, () => navigate(isEdit ? `${ROUTE_COMPLETE_PROFILE}` : ROUTE_SKILLSET_NEW))
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <HomeIcon  />
            </InputAdornment> 
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <LogoutIcon  />
            </InputAdornment> 
          </Box>
        </Box>
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        {" "}
        {/* Add this Box wrapper */}
        <Tabs
          variant="scrollable"
          // textColor="#181818"
          indicatorColor="primary"
          value={selectedTab} // Use selectedTab state variable
          onChange={handleTabChange} // Use handleTabChange function
          sx={{
            "& .MuiTabs-indicator": {
              height: "6px",
              backgroundColor: "#52C6AE !important",
              borderRadius: "5px 5px 5px 5px", // Rounded corners
              top: 6, // Position at the bottom
              bottom: "auto", // Disable top positioning
              transform: "translateY(-100%)", // Move indicator to top
            },
          }}
        >
          {degrees.map((degree, index) => (
            <Tab key={index} label={degree} />
          ))}
        </Tabs>
      </Box>

      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
        <Box display="flex" alignItems="center" mb={1} padding="5px 16px">
          <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
          <StyledTextBox p={1} mt={2} ml={1}>
            <Box px={1}>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                2/2
              </Typography>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                Do you have experience or internship to flaunt?
              </Typography>
            </Box>
          </StyledTextBox>
        </Box>

        <Grid justifyContent="flex-end" container spacing={4} pl={2} mb={1}>
          {jobTypes.map((jobType) => (
            <Grid justifyContent="flex-end" item xs={4} key={jobType}>
              <FormControlLabel
                value={jobType}
                control={<Radio sx={{ width: "auto" }} checked={SelectedJob === jobType} />} // Adjust the width here
                label={
                  <JobType>
                    <Typography sx={{ width: "100px" }}>{jobType}</Typography>
                  </JobType>
                }
                onChange={handleJobChange}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {SelectedJob === "Yes" ? (
          <Box>
            <Box display="flex" alignItems="center" mx={2} mb={1}>
              <StyledTextBox1 px={2} py={1}>
                Let&apos;s start with your most recent experience.
              </StyledTextBox1>
            </Box>
            {experienceFields?.map((el, index) => {
              return (
                <Box
                  key={el?.id}
                  display="flex"
                  flexDirection="column"
                  p={2}
                  bgcolor="#ffffff"
                  sx={{ borderRadius: "20px 20px 0px 0px" }}
                >
                  <Typography mt={2}>Company Name</Typography>
                  <InputField control={control} name={`experiences.${index}.companyName`} />
                  <Typography mt={2}>Designation</Typography>
                  <InputField control={control} name={`experiences.${index}.designation`} />
                  <Typography mt={2}>Function</Typography>

                  <MultiSelect
                    name={`experiences.${index}.function`}
                    control={control}
                    options={functions?.map((el: any) => ({ label: el, value: el })) || []}
                    creatable={true}
                    multiple={false}
                  />
                  <Box>
                    <Typography mt={2}>Job Type</Typography>
                  </Box>

                  <MultiSelect
                    name={`experiences.${index}.jobType`}
                    control={control}
                    options={getKeyValuePairs(JOB_TYPES)}
                  />
                  <Box>
                    <Typography mt={2}>Work Mode</Typography>
                  </Box>

                  <MultiSelect
                    name={`experiences.${index}.workMode`}
                    control={control}
                    options={getKeyValuePairs(WORK_MODES)}
                  />
                  <Box mt={1}>
                    <Controller
                      control={control}
                      name={`experiences.${index}.working`}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} color="primary" />}
                          label="I'm currently working here"
                        />
                      )}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography mt={2}>Joining Date</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          control={control}
                          name={`experiences.${index}.startDate`}
                          render={({ field, fieldState: { error } }) => (
                            <Box>
                              <Box
                                sx={{
                                  width: "100%",
                                  borderRadius: "100px",
                                  boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                  border: "none",
                                  mt: 1,
                                }}
                              >
                                <DatePicker
                                  // label="Select Date"
                                  sx={{
                                    "& input": {
                                      textAlign: "center",
                                      padding: "12px 12px",
                                    },
                                  }}
                                  value={field.value ? dayjs(field.value) : null}
                                  onChange={(date) => field.onChange(date ? date.toDate() : null)}
                                  maxDate={dayjs()}
                                  format="DD/MM/YYYY"
                                />
                              </Box>
                              <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                                {error ? error?.message : " "}
                              </FormHelperText>
                            </Box>
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography mt={2}>End Date </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          control={control}
                          name={`experiences.${index}.endDate`}
                          render={({ field, fieldState: { error } }) => (
                            <Box>
                              <Box
                                sx={{
                                  width: "100%",
                                  borderRadius: "100px",
                                  boxShadow: "inset 0 0 11px 2px #dfd9d9",
                                  border: "none",
                                  mt: 1,
                                }}
                              >
                                <DatePicker
                                  // label="Select Date"
                                  sx={{
                                    "& input": {
                                      padding: "12px 16px",
                                    },
                                  }}
                                  disabled={!!watch(`experiences.${index}.working`)}
                                  value={field.value ? dayjs(field.value) : null}
                                  onChange={(date) => field.onChange(date ? date.toDate() : null)}
                                  maxDate={dayjs()}
                                  format="DD/MM/YYYY"
                                />
                              </Box>
                              <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                                {error ? error?.message : " "}
                              </FormHelperText>
                            </Box>
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={9}>
                      <Typography mt={2}>Experience Summary (200 words)</Typography>
                    </Grid>
                    <Grid item xs={3} display="flex" justifyContent="flex-end">
                      <Typography mt={2}>Optional</Typography>
                    </Grid>
                  </Grid>
                  <InputField control={control} name={`experiences.${index}.summary`} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography>Work Link </Typography>
                    </Grid>
                    <Grid item xs={6} display="flex" justifyContent="flex-end">
                      <Typography>Optional</Typography>
                    </Grid>
                  </Grid>
                  <InputField control={control} name={`experiences.${index}.link`} />
                  {/* <Typography mt={2}>+ Add Link</Typography> */}
                  {experienceFields?.length >= 1 ? (
                    <Button onClick={() => removeExperience(index)} sx={styles.button} variant="contained" size="small">
                      <img src="/images/delete-bin-line.png" alt="" width="18px" /> Delete
                    </Button>
                  ) : null}
                </Box>
              );
            })}

            <Box mx={2} display="flex" alignItems="center" pb={3}>
              <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
              <StyledTextBox px={1} mt={2} ml={1}>
                <Box p={1}>
                  <Typography
                    style={{
                      fontFamily: '"DM Sans", sans-serif !important',
                      fontSize: "14px !important",
                      fontWeight: "medium !important",
                      color: "#1F2235",
                    }}
                  >
                    1/2
                  </Typography>
                  <Typography
                    style={{
                      fontFamily: '"DM Sans", sans-serif !important',
                      fontSize: "14px !important",
                      fontWeight: "medium !important",
                      color: "#1F2235",
                    }}
                  >
                    Would you like to add another experience?
                  </Typography>
                </Box>
              </StyledTextBox>
            </Box>
            <Box sx={{ mt: 2, textAlign: "right", mr: 2, mb: 2 }}>
              <Button
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  width: "75px",
                  cursor: "pointer",
                  border: "1px dashed #756DE9",
                  color: "#756DE9",
                  backgroundColor: "#fff !important",
                }}
                className="px-3 py-2 me-2 mb-2 roundedone"
                onClick={() => appendExperience(experience)}
              >
                Yes
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  width: "75px",
                  cursor: "pointer",
                  border: "",
                  color: "",
                  backgroundColor: "#756DE9",
                }}
                className="px-3 py-2 me-2 mb-2 roundedone"
                // onClick={() => setShow(false)}
              >
                No
              </Button>
            </Box>
          </Box>
        ) : null}

        <Box display="flex" flexDirection="column" p={2} bgcolor="#ffffff" sx={{ borderRadius: "20px 20px 0px 0px" }}>
          {location?.search?.includes("edit") ? (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                justifyContent: "center",
                alignItems: "center",
                padding: "15px 64px",
                marginTop: "20px",
                marginBottom: "15px",
                fontSize: "16px",
                fontWeight: 600,
                color: "#ffffff",
                backgroundColor: "#756DE9",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "95px",
                textTransform: "none",
              }}
            >
              Save
            </Button>
          ) : (
            <Box display="flex">
              <Button
                sx={{
                  backgroundColor: "white", // Change background color to white
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "25%",
                  color: "#000000",
                  "&:hover ": {
                    backgroundColor: "#f5f5f5", // Change hover background color if needed
                  },
                }}
                variant="contained"
                size="small"
                style={{ padding: "15px 40px" }}
                className="w-100 mt-2 btn-pad"
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Box flexGrow={1}></Box>
              {SelectedJob === "Yes" ? (
                <Button
                  type="submit"
                  sx={{
                    backgroundColor: "#756DE9",
                    borderRadius: "99px",
                    fontSize: "16px",
                    textTransform: "capitalize",
                    width: "70%",
                    paddingY: "15px",
                    paddingX: 0,
                    "&:hover": {
                      backgroundColor: "#756DE9",
                    },
                  }}
                  variant="contained"
                  size="small"
                  className="w-100 mt-2 btn-pad"
                  disabled={!!ajaxCallsInProgress}
                >
                  Next Up: Specialization
                </Button>
              ) : (
                <Button
                  onClick={() => navigate(ROUTE_SKILLSET_NEW)}
                  sx={{
                    backgroundColor: "#756DE9",
                    borderRadius: "99px",
                    fontSize: "16px",
                    textTransform: "capitalize",
                    width: "70%",
                    paddingY: "15px",
                    paddingX: 0,
                    "&:hover": {
                      backgroundColor: "#756DE9",
                    },
                  }}
                  variant="contained"
                  size="small"
                  className="w-100 mt-2 btn-pad"
                >
                  Next Up: Specialization
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExperienceNew;
